/**
 * Which worlds of a cave world should have their data (terrain and the like) in memory,
 * from where the player is. Pure: the game feeds it the player's place and distances,
 * and loads or drops worlds as it says.
 *
 * - The world you're in is loaded, and so are both worlds of a cave you're in (entrance
 *   or interior): you come out in one of them.
 * - Near a cave's mouth, the world beyond it is loaded too, so walking in doesn't wait,
 *   and turning back at the mouth doesn't drop and reload it: it loads within `near`
 *   metres of the mouth and stays until you're `far` away.
 * - A world that's no longer wanted is dropped only after `linger` seconds, so hovering
 *   at a boundary doesn't load and drop it over and over.
 */
export interface LoadPolicyOptions {
  near: number;
  far: number;
  linger: number;
}

export interface LoadInput {
  /** Where the player is: in world `hollow`, or in the cave of `link` (entrance or interior). */
  place: { hollow: number } | { link: number };
  /** The caves: the worlds each joins, and the player's distance to its mouth in each (m). */
  links: { from: number; to: number; mouthDistance: [number, number] }[];
  /** Seconds, from any fixed start. */
  now: number;
}

export interface LoadActions {
  load: number[];
  unload: number[];
}

const DEFAULTS: LoadPolicyOptions = { near: 150, far: 260, linger: 8 };

export class LoadPolicy {
  private readonly options: LoadPolicyOptions;
  /** Worlds the policy has asked to be loaded, and since when each has been unwanted. */
  readonly loaded = new Set<number>();
  private readonly unwantedSince = new Map<number, number>();

  constructor(options: Partial<LoadPolicyOptions> = {}) {
    this.options = { ...DEFAULTS, ...options };
  }

  /** The worlds wanted now. */
  wanted(input: LoadInput): Set<number> {
    const want = new Set<number>();
    const { place, links } = input;
    if ('link' in place) {
      const l = links[place.link];
      want.add(l.from);
      want.add(l.to);
      return want;
    }
    want.add(place.hollow);
    for (const l of links) {
      if (l.from !== place.hollow && l.to !== place.hollow) continue;
      const here = l.from === place.hollow;
      const beyond = here ? l.to : l.from;
      const reach = this.loaded.has(beyond) ? this.options.far : this.options.near;
      if (l.mouthDistance[here ? 0 : 1] < reach) want.add(beyond);
    }
    return want;
  }

  /** What to load and drop now; call a few times a second. */
  update(input: LoadInput): LoadActions {
    const want = this.wanted(input);
    const load: number[] = [];
    const unload: number[] = [];
    for (const i of want) {
      this.unwantedSince.delete(i);
      if (!this.loaded.has(i)) {
        this.loaded.add(i);
        load.push(i);
      }
    }
    for (const i of [...this.loaded]) {
      if (want.has(i)) continue;
      const since = this.unwantedSince.get(i);
      if (since === undefined) this.unwantedSince.set(i, input.now);
      else if (input.now - since >= this.options.linger) {
        this.loaded.delete(i);
        this.unwantedSince.delete(i);
        unload.push(i);
      }
    }
    return { load, unload };
  }
}
